import {
  Button,
  Checkbox,
  CheckboxProps,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Text,
  tokens,
  Tooltip,
} from '@fluentui/react-components';
import { Delete28Regular } from '@fluentui/react-icons';
import { CellContext } from '@tanstack/react-table';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PermissionChangeRequest, FolderPermissionResponse, FolderPermissionsResponse } from '../../../../api/types.gen';
import { PermissionsDialogForm } from '../modifyPermissionsDialog/PermissionsDialogForm';
import { PermissionTableFeatures } from './permissionTableFeatures';

interface IActionsCellProps {
  folderInfo: FolderPermissionsResponse;
  cellContext: CellContext<PermissionTableFeatures, FolderPermissionResponse>;
  handleEdit: (permissionInfo: PermissionChangeRequest, applyRecursive: boolean) => Promise<void>;
  handleDelete: (permissionInfo: FolderPermissionResponse, applyRecursive: boolean) => Promise<void>;
  isLoading: boolean;
}

export function ActionsCell({ cellContext: { row }, handleDelete, handleEdit, folderInfo, isLoading }: IActionsCellProps) {
  const { t } = useTranslation();
  const [isDeletePopoverOpen, setIsDeletePopoverOpen] = useState<boolean>(false);
  const [applyRecursive, setApplyRecursive] = useState<CheckboxProps['checked']>(false);

  const permission: FolderPermissionResponse | null = row.original;

  if (!permission || permission.user === 'Anonymous/Default') {
    return null;
  }

  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <PermissionsDialogForm
        onSave={handleEdit}
        isLoading={isLoading}
        dialogTitle={t('permissions.modifyAction.editTitle')}
        openingButtonLabel={t('permissions.modifyAction.editTitle')}
        folderInfo={folderInfo}
        initialPermissions={{
          users: [{ displayName: permission.user, mail: permission.email }],
          canCreateItems: permission.canCreateItems,
          canCreateSubfolders: permission.canCreateSubFolders,
          isFolderContact: permission.isFolderContact,
          isFolderOwner: permission.isFolderOwner,
          isFolderVisible: permission.isFolderVisible,
          permissionLevel: permission.permissionLevel,
          deleteItems: permission.deleteItems,
          editItems: permission.editItems,
          readItems: permission.readItems,
        }}
      />
      <Popover open={isDeletePopoverOpen} onOpenChange={(_, { open }) => setIsDeletePopoverOpen(open)}>
        <Tooltip content={t('permissions.deleteAction.buttonLabel')} relationship="label">
          <PopoverTrigger disableButtonEnhancement>
            <Button
              appearance="transparent"
              size="medium"
              disabled={isLoading}
              icon={<Delete28Regular color={tokens.colorStatusDangerForeground1} />}
            />
          </PopoverTrigger>
        </Tooltip>
        <PopoverSurface tabIndex={-1}>
          <div style={{ display: 'grid', gap: 4 }}>
            <Text size={400} weight="semibold">
              {t('permissions.deleteAction.confirmTitle')}
            </Text>
            <div style={{ display: 'flex', gap: 4, justifyContent: 'end' }}>
              <Checkbox
                checked={applyRecursive}
                onChange={(_, data) => setApplyRecursive(data.checked)}
                label={t('permissions.modifyAction.applyRecursive')}
                labelPosition="after"
              />
              <Button appearance="secondary" onClick={() => setIsDeletePopoverOpen(false)}>
                {t('permissions.deleteAction.cancelLabel')}
              </Button>
              <Button
                appearance="primary"
                size="medium"
                disabled={isLoading}
                onClick={() => {
                  handleDelete(row.original, applyRecursive as boolean).then(() => setIsDeletePopoverOpen(false));
                }}
              >
                {t('permissions.deleteAction.buttonLabel')}
              </Button>
            </div>
          </div>
        </PopoverSurface>
      </Popover>
    </div>
  );
}
